import React, { Component } from 'react';
import AsyncButton from '@components/button/AsyncButton';
import SmartSelect from '@components/input/SmartSelect';
import UserSelect from '@components/modelSelect/UserSelect';
import SidebarLayout from '../layout/SidebarLayout';
import OptionService from "@services/OptionService";
import User from "@models/User";
import EntityManager from "@services/EntityManager";
import DOMService from "@services/DOMService";
import SmartDatePicker from "@components/input/SmartDatePicker";
import SmartInputText from '@components/input/SmartInputText';
import Sector from '@models/Sector';
import SectorSelect from '@components/modelSelect/SectorSelect';
import UserSectorArea from '@models/UserSectorArea';
import UserDailyPlanning from '@models/UserDailyPlanning';

interface DailyPlanningFormRow {
    id?: number;
    dayOfWeek: number;
    startTime: string | null;
    endTime: string | null;
    creneau1: number | null;
    creneau2: number | null;
    creneau3: number | null;
    creneau4: number | null;
    creneau5: number | null;
}

const DAILY_DAYS = [
    { dayOfWeek: 1, label: "Lundi" },
    { dayOfWeek: 2, label: "Mardi" },
    { dayOfWeek: 3, label: "Mercredi" },
    { dayOfWeek: 4, label: "Jeudi" },
    { dayOfWeek: 5, label: "Vendredi" },
];

const DAILY_SLOT_LABELS = {
    creneau1: "C1 (08h-10h)",
    creneau2: "C2 (10h-12h)",
    creneau3: "C3 (13h-15h)",
    creneau4: "C4 (15h-17h)",
    creneau5: "C5 (18h+ / nuit)",
};

export interface SetUserGodinLibProps {
    sectorId: number | null,
    worker: User | null
}

export interface SetUserGodinLibState {
    selectedWorker: User | null;
    selectedQuartier: string | null;
    quartiersOption: { label: string; id: string }[];
    assignments: UserSectorArea[];
    editingAssignmentId: number | null;
    selectedAssignmentSector: Sector | null;
    selectedAssignmentQuartier: string | null;
    selectedAssignmentQuartiers: string[];
    startSelectedDate: Date,
    endSelectedDate: Date,
    creneau1: number,
    creneau2: number,
    creneau3: number,
    creneau4: number,
    creneau5: number,
    nightSlotEnabled: boolean,
    listSector: Sector[],
    selectedSector: Sector | null,
    dailyPlanningsByDay: Record<number, DailyPlanningFormRow>
}

class SetUserGodinLib extends Component<SetUserGodinLibProps, SetUserGodinLibState> {
    constructor(props: SetUserGodinLibProps) {
        super(props);
        let quartierOptions = [];

        this.state = {
            assignments: [],
            editingAssignmentId: null,
            selectedAssignmentSector: null,
            selectedAssignmentQuartier: null,
            selectedAssignmentQuartiers: [],
            listSector: [],
            selectedWorker: props.worker || null,
            selectedQuartier: props.worker?.quartier || '',
            quartiersOption: quartierOptions,
            endSelectedDate: props.worker?.endRdv ? new Date(props.worker.endRdv) : new Date(),
            startSelectedDate: props.worker?.startRdv ? new Date(props.worker.startRdv) : new Date(),
            creneau1: props.worker?.creneau1 || 4,
            creneau2: props.worker?.creneau2 || 4,
            creneau3: props.worker?.creneau3 || 4,
            creneau4: props.worker?.creneau4 || 4,
            creneau5: props.worker?.creneau5 || 4,
            nightSlotEnabled: Boolean(props.worker?.nightSlotEnabled),
            selectedSector: props.worker?.sector || null,
            dailyPlanningsByDay: this.buildDefaultDailyRows(props.worker || null),
        };
    }

    componentDidMount() {
        if (this.props.worker?.sectorId) {
            this.loadQuartier(this.props.worker.sectorId);
        }
        if (this.state.selectedWorker?.id) {
            this.loadAssignments(this.state.selectedWorker.id, this.state.selectedSector?.id || null);
            this.loadDailyPlannings(this.state.selectedWorker.id, this.state.selectedWorker);
        }
    }

    buildDefaultDailyRows = (worker: User | null): Record<number, DailyPlanningFormRow> => {
        const rows: Record<number, DailyPlanningFormRow> = {};
        DAILY_DAYS.forEach(({ dayOfWeek }) => {
            rows[dayOfWeek] = {
                dayOfWeek,
                startTime: null,
                endTime: null,
                creneau1: worker?.creneau1 ?? 4,
                creneau2: worker?.creneau2 ?? 4,
                creneau3: worker?.creneau3 ?? 4,
                creneau4: worker?.creneau4 ?? 4,
                creneau5: worker?.creneau5 ?? null,
            };
        });
        return rows;
    }

    loadQuartier = async (id) => {
        await OptionService.loadQuartier(id);
        if (OptionService.quartiers[id]) {
            let quartierOptions = [];
            OptionService.quartiers[id].forEach((quartier) => {
                quartierOptions.push({ label: quartier, id: quartier });
            });
            this.setState({ quartiersOption: quartierOptions });
        }
    }

    loadAssignments = async (userId: number, sectorId: number | null = null) => {
        const response = await EntityManager.all<UserSectorArea>(UserSectorArea, {
            params: { user_id: userId },
            with: ['sector'],
        });
        const models = response.models || [];
        const selectedAssignmentQuartiers = sectorId
            ? models.filter((a) => a.sectorId === sectorId).map((a) => a.quartier)
            : models.map((a) => a.quartier);
        this.setState({
            assignments: models,
            selectedAssignmentQuartiers,
        });
    }

    loadDailyPlannings = async (userId: number, worker: User | null = null) => {
        const response = await EntityManager.all<UserDailyPlanning>(UserDailyPlanning, {
            params: { user_id: userId },
        });

        const rows = this.buildDefaultDailyRows(worker);
        (response.models || []).forEach((planning) => {
            rows[planning.dayOfWeek] = {
                id: planning.id,
                dayOfWeek: planning.dayOfWeek,
                startTime: planning.startTime || null,
                endTime: planning.endTime || null,
                creneau1: planning.creneau1 ?? 4,
                creneau2: planning.creneau2 ?? 4,
                creneau3: planning.creneau3 ?? 4,
                creneau4: planning.creneau4 ?? 4,
                creneau5: planning.creneau5 ?? null,
            };
        });

        this.setState({ dailyPlanningsByDay: rows });
    }

    setDailyPlanningValue = (dayOfWeek: number, key: keyof DailyPlanningFormRow, value: any) => {
        this.setState((prev) => ({
            dailyPlanningsByDay: {
                ...prev.dailyPlanningsByDay,
                [dayOfWeek]: {
                    ...prev.dailyPlanningsByDay[dayOfWeek],
                    [key]: value,
                },
            },
        }));
    }

    toSecondsTime = (value: string | null): string | null => {
        if (!value) return null;
        return value.length === 5 ? `${value}:00` : value;
    }

    saveDailyPlannings = async (userId: number) => {
        const { dailyPlanningsByDay } = this.state;
        for (const day of DAILY_DAYS) {
            const row = dailyPlanningsByDay[day.dayOfWeek];
            const startTime = this.toSecondsTime(row.startTime);
            const endTime = this.toSecondsTime(row.endTime);
            const hasAnyValue =
                Boolean(startTime) ||
                Boolean(endTime) ||
                row.creneau1 !== null ||
                row.creneau2 !== null ||
                row.creneau3 !== null ||
                row.creneau4 !== null ||
                row.creneau5 !== null;

            if (!hasAnyValue && row.id) {
                await EntityManager.delete(new UserDailyPlanning({ id: row.id }));
                continue;
            }

            if (!hasAnyValue) {
                continue;
            }

            const payload = {
                id: row.id,
                userId,
                dayOfWeek: row.dayOfWeek,
                startTime,
                endTime,
                creneau1: row.creneau1,
                creneau2: row.creneau2,
                creneau3: row.creneau3,
                creneau4: row.creneau4,
                creneau5: row.creneau5,
            };

            if (row.id) {
                await EntityManager.update(new UserDailyPlanning(payload));
            } else {
                await EntityManager.create(new UserDailyPlanning(payload));
            }
        }
    }

    addAssignment = async () => {
        const { selectedWorker, selectedAssignmentSector, selectedAssignmentQuartier, editingAssignmentId } = this.state;
        if (!selectedWorker || !selectedAssignmentSector || !selectedAssignmentQuartier) return;

        if (editingAssignmentId) {
            await EntityManager.update(
                new UserSectorArea({
                    id: editingAssignmentId,
                    userId: selectedWorker.id,
                    sectorId: selectedAssignmentSector.id,
                    quartier: selectedAssignmentQuartier,
                })
            );
        } else {
            await EntityManager.create(
                new UserSectorArea({
                    userId: selectedWorker.id,
                    sectorId: selectedAssignmentSector.id,
                    quartier: selectedAssignmentQuartier,
                })
            );
        }

        await this.loadAssignments(selectedWorker.id);
        this.setState({ selectedAssignmentQuartier: null, editingAssignmentId: null });
    }

    removeAssignment = async (assignment: UserSectorArea) => {
        const { selectedWorker } = this.state;
        if (!selectedWorker) return;
        await EntityManager.delete(assignment);
        await this.loadAssignments(selectedWorker.id);
    }

    editAssignment = (assignment: UserSectorArea) => {
        this.setState((prev) => ({
            editingAssignmentId: assignment.id,
            selectedAssignmentSector: assignment.sector || prev.selectedSector,
            selectedAssignmentQuartier: assignment.quartier,
            selectedAssignmentQuartiers: [assignment.quartier],
        }));
    }

    cancelEditAssignment = () => {
        this.setState((prev) => ({
            editingAssignmentId: null,
            selectedAssignmentQuartier: null,
            selectedAssignmentSector: prev.selectedSector,
        }));
    }

    toggleSelectedAssignmentQuartier = (quartier: string) => {
        this.setState((prev) => {
            const isSelected = prev.selectedAssignmentQuartiers.includes(quartier);
            return {
                selectedAssignmentQuartiers: isSelected
                    ? prev.selectedAssignmentQuartiers.filter((q) => q !== quartier)
                    : [...prev.selectedAssignmentQuartiers, quartier],
            };
        });
    }

    syncAssignmentsFromMultiSelection = async () => {
        const { selectedWorker, selectedSector, assignments, selectedAssignmentQuartiers } = this.state;
        if (!selectedWorker || !selectedSector) return;

        const existingForCurrentSector = assignments.filter((a) => a.sectorId === selectedSector.id);
        const existingQuartiers = new Set(existingForCurrentSector.map((a) => a.quartier));
        const wantedQuartiers = new Set(selectedAssignmentQuartiers);

        const toCreate = Array.from(wantedQuartiers).filter((quartier) => !existingQuartiers.has(quartier));
        const toDelete = existingForCurrentSector.filter((a) => !wantedQuartiers.has(a.quartier));

        for (const quartier of toCreate) {
            await EntityManager.create(
                new UserSectorArea({
                    userId: selectedWorker.id,
                    sectorId: selectedSector.id,
                    quartier,
                })
            );
        }

        for (const assignment of toDelete) {
            await EntityManager.delete(assignment);
        }

        await this.loadAssignments(selectedWorker.id);
        this.setState({ editingAssignmentId: null, selectedAssignmentQuartier: null });
    }

    clearUnusedConfigurations = async () => {
        const { selectedWorker, assignments } = this.state;
        if (!selectedWorker) return;

        const confirmed = globalThis.confirm(
            "Supprimer toutes les affectations multiples de cet agent et vider la configuration legacy ?"
        );
        if (!confirmed) return;

        try {
            for (const assignment of assignments) {
                await EntityManager.delete(assignment);
            }

            selectedWorker.update({ sectorId: null, quartier: null });
            await EntityManager.update(selectedWorker);

            await this.loadAssignments(selectedWorker.id);
            this.setState({
                selectedSector: null,
                selectedQuartier: null,
                selectedAssignmentQuartier: null,
                selectedAssignmentQuartiers: [],
                editingAssignmentId: null,
            });
        } catch (error) {
            console.error("clearUnusedConfigurations failed", error);
            globalThis.alert("Impossible de vider la configuration. Vérifiez les dépendances puis réessayez.");
        }
    }

    deleteWorkerSafely = async () => {
        const { selectedWorker, assignments, selectedSector, selectedQuartier } = this.state;
        if (!selectedWorker) return;

        const hasMultipleAssignments = assignments.length > 0;
        const hasLegacyConfig = Boolean(selectedSector || selectedQuartier || selectedWorker.sectorId || selectedWorker.quartier);

        if (hasMultipleAssignments || hasLegacyConfig) {
            globalThis.alert(
                "Suppression bloquée: retirez d'abord toutes les affectations et la configuration existante."
            );
            return;
        }

        const confirmed = globalThis.confirm(
            `Confirmer la suppression définitive de l'agent ${selectedWorker.firstname} ${selectedWorker.lastname} ?`
        );
        if (!confirmed) return;

        try {
            await EntityManager.delete(selectedWorker);
            DOMService.closeSidebar();
            globalThis.location.reload();
        } catch (error) {
            console.error("deleteWorkerSafely failed", error);
            globalThis.alert(
                "Suppression impossible: cet agent est probablement encore utilisé (RDV, interventions ou autres références)."
            );
        }
    }

    onSubmit = async () => {
        const { selectedWorker, selectedQuartier, endSelectedDate, startSelectedDate, creneau1, creneau2, creneau3, creneau4, creneau5, nightSlotEnabled, selectedSector } = this.state;
        if (!selectedWorker || !selectedSector) return;
        selectedWorker.update({ quartier: selectedQuartier, sectorId: selectedSector.id, endRdv: endSelectedDate.toISOString().split('T')[0], startRdv: startSelectedDate.toISOString().split('T')[0], creneau1, creneau2, creneau3, creneau4, creneau5, nightSlotEnabled })
        await EntityManager.update(selectedWorker);
        await this.saveDailyPlannings(selectedWorker.id);
        DOMService.closeSidebar()
        globalThis.location.reload()
    };

    render() {
        const { selectedWorker, selectedQuartier, quartiersOption, startSelectedDate, endSelectedDate, creneau1, creneau2, creneau3, creneau4, creneau5, nightSlotEnabled, selectedSector, assignments, selectedAssignmentQuartier, dailyPlanningsByDay, editingAssignmentId, selectedAssignmentQuartiers } = this.state;
        return (
            <SidebarLayout
                title={"Assignation d'un utilisateur à un quartier"}
                bottomArea={<AsyncButton className="btn btn-primary w-100" loading={false} onClick={this.onSubmit}>Assigner</AsyncButton>}
            >
                {this.state.listSector &&
                    <>
                        <SectorSelect
                            label="Sélectionner un secteur"
                            placeholder={selectedSector ? selectedSector.name : "Sélectionner un secteur"}
                            value={selectedSector}
                            onChange={(selected) => {
                                this.setState(() => ({ selectedSector: selected }));
                                OptionService.loadQuartier(selected.id).then(() => { this.loadQuartier(selected.id) });
                            }}
                        />

                        {
                                <div className="fadeInDown">
                                    <UserSelect
                                        value={selectedWorker}
                                        label="Sélectionner un agent"
                                        placeholder={selectedWorker ? (selectedWorker.firstname + " " + selectedWorker.lastname) : "Choisir un agent"}
                                        containerClassName="mb-3"
                                        params={{ scope: "onlyWorkers" }}
                                        onChange={async (selected) => {
                                            if (!selected) return;
                                            const sameSector = selectedSector ? selectedSector.id === selected.sectorId : true;
                                            this.setState(
                                                {
                                                    selectedWorker: selected || null,
                                                    selectedQuartier: sameSector ? selected.quartier : null,
                                                    selectedAssignmentSector: selectedSector || selected.sector || null,
                                                    selectedAssignmentQuartier: null,
                                                    startSelectedDate: selected.startRdv ? new Date(selected.startRdv) : new Date(),
                                                    endSelectedDate: selected.endRdv ? new Date(selected.endRdv) : new Date(),
                                                    creneau1: selected.creneau1 ? selected.creneau1 : 4,
                                                    creneau2: selected.creneau2 ? selected.creneau2 : 4,
                                                    creneau3: selected.creneau3 ? selected.creneau3 : 4,
                                                    creneau4: selected.creneau4 ? selected.creneau4 : 4,
                                                    creneau5: selected.creneau5 ? selected.creneau5 : 4,
                                                    nightSlotEnabled: Boolean(selected.nightSlotEnabled),
                                                });
                                            await this.loadAssignments(selected.id, selectedSector?.id || null);
                                            await this.loadDailyPlannings(selected.id, selected);
                                        }}
                                    />
                                    <SmartSelect
                                        label="Quartier legacy (compat)"
                                        placeholder="Choisir un quartier"
                                        options={quartiersOption}
                                        value={selectedQuartier ? quartiersOption.find(o => o.id === selectedQuartier) : null}
                                        onChange={(selected) => this.setState({ selectedQuartier: selected?.id || null })}
                                    />
                                    <div className="mt-3 p-2 border rounded">
                                        <div className="mb-2"><b>Affectations agent</b></div>

                                        <div className="mb-3 p-2 border rounded">
                                            <div><b>1) Sélection multiple (recommandé)</b></div>
                                            <small>Sélectionne les quartiers à conserver pour ce secteur, puis applique.</small>
                                            <div className="mt-2 mb-2">
                                                {quartiersOption.map((option) => {
                                                    const isSelected = selectedAssignmentQuartiers.includes(option.id);
                                                    return (
                                                        <button
                                                            key={option.id}
                                                            type="button"
                                                            className={`btn btn-sm mr-2 mb-2 ${isSelected ? "btn-primary" : "btn-outline-secondary"}`}
                                                            onClick={() => this.toggleSelectedAssignmentQuartier(option.id)}
                                                        >
                                                            {isSelected ? "✓ " : ""}{option.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <AsyncButton className="btn btn-outline-primary mt-1" loading={false} onClick={this.syncAssignmentsFromMultiSelection}>
                                                Appliquer la sélection multiple
                                            </AsyncButton>
                                        </div>

                                        <div className="mb-3 p-2 border rounded">
                                            <div><b>2) Action unitaire</b></div>
                                            <small>Ajoute ou modifie une seule affectation.</small>
                                            <SmartSelect
                                                label="Quartier (action unitaire)"
                                                placeholder="Choisir un quartier"
                                                options={quartiersOption}
                                                value={selectedAssignmentQuartier ? quartiersOption.find(o => o.id === selectedAssignmentQuartier) : null}
                                                onChange={(selected) => this.setState({ selectedAssignmentQuartier: selected?.id || null })}
                                            />
                                            <div className="d-flex align-items-center gap-2">
                                                <AsyncButton className="btn btn-outline-primary mt-2" loading={false} onClick={this.addAssignment}>
                                                    {editingAssignmentId ? "Mettre à jour l'affectation" : "Ajouter cette affectation"}
                                                </AsyncButton>
                                                {Boolean(editingAssignmentId) && (
                                                    <button className="btn btn-link mt-2" onClick={this.cancelEditAssignment}>
                                                        Annuler
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="mb-3 p-2 border rounded">
                                            <div><b>3) Actions agent</b></div>
                                            <small>Actions sensibles, appliquées uniquement à l'agent sélectionné.</small>
                                            <div className="d-flex align-items-center gap-2 flex-wrap">
                                                <button className="btn btn-outline-danger mt-2" onClick={this.clearUnusedConfigurations}>
                                                    Vider les affectations de cet agent
                                                </button>
                                                <button className="btn btn-danger mt-2" onClick={this.deleteWorkerSafely}>
                                                    Supprimer l'agent (vérifié)
                                                </button>
                                            </div>
                                        </div>

                                        <div className="mt-2">
                                            <b>Affectations enregistrées</b>
                                        </div>
                                        <div className="mt-2">
                                            {assignments.length === 0 && <div>Aucune affectation enregistrée.</div>}
                                            {assignments.map((assignment) => (
                                                <div key={assignment.id} className="d-flex justify-content-between align-items-center mb-2">
                                                    <span>{assignment.sector?.name || `Secteur #${assignment.sectorId}`} - {assignment.quartier}</span>
                                                    <div className="d-flex gap-2">
                                                        <button className="btn btn-sm btn-outline-secondary" onClick={() => this.editAssignment(assignment)}>
                                                            Modifier
                                                        </button>
                                                        <button className="btn btn-sm btn-outline-danger" onClick={() => this.removeAssignment(assignment)}>
                                                            Supprimer
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <SmartDatePicker label="Du ?" containerClassName='mb-3' value={startSelectedDate} onChange={(selected) => this.setState({ startSelectedDate: selected })} />
                                    <SmartDatePicker label="Jusqu'au ?" containerClassName='mb-3' value={endSelectedDate} onChange={(selected) => this.setState({ endSelectedDate: selected })} />
                                    {
                                        Boolean(selectedSector) &&
                                        Boolean(selectedWorker?.sectorId) &&
                                        selectedWorker?.sectorId !== selectedSector?.id &&
                                        <>
                                            <br />
                                            <span>Attention cet employé est déjà assigné a un autre secteur.</span>
                                        </>

                                    }
                                    <div>Créneau 8-10h</div>
                                    <SmartInputText value={creneau1} onChange={(value) => this.setState({ creneau1: value || null })} placeholder={"Créneau 8-10h"} />
                                    <br />
                                    <div>Créneau 10-12h</div>
                                    <SmartInputText value={creneau2} onChange={(value) => this.setState({ creneau2: value || null })} placeholder={"Créneau 10-12h"} />
                                    <br />
                                    <div>Créneau 13-15h</div>
                                    <SmartInputText value={creneau3} onChange={(value) => this.setState({ creneau3: value || null })} placeholder={"Créneau 13-15h"} />
                                    <br />
                                    <div>Créneau 15-17h</div>
                                    <SmartInputText value={creneau4} onChange={(value) => this.setState({ creneau4: value || null })} placeholder={"Créneau 15-17h"} />
                                    <br />
                                    <div className="mb-2"><b>Mode nuit</b></div>
                                    <button
                                        type="button"
                                        className={`btn mb-2 ${nightSlotEnabled ? "btn-success" : "btn-outline-light"}`}
                                        onClick={() => this.setState({ nightSlotEnabled: !nightSlotEnabled })}
                                    >
                                        {nightSlotEnabled ? "Mode nuit activé (après 18h = C5)" : "Activer le mode nuit (après 18h = C5)"}
                                    </button>
                                    <small className="d-block text-muted mb-2">
                                        {nightSlotEnabled
                                            ? "Mode nuit ACTIF : après 18h, la capacité C5 est utilisée."
                                            : "Mode nuit DÉSACTIVÉ (par défaut) : avant 18h, c’est la capacité C4 qui s’applique."}
                                    </small>
                                    {nightSlotEnabled && (
                                        <>
                                            <div>Créneau 18h+ (nuit, C5)</div>
                                            <SmartInputText value={creneau5} onChange={(value) => this.setState({ creneau5: value || null })} placeholder={"Capacité C5 (18h+)"} />
                                        </>
                                    )}
                                    <div className="mt-4 p-2 border rounded">
                                        <div className="mb-2"><b>Configuration journalière (Phase 4)</b></div>
                                        <small className="d-block mb-3">
                                            Définis pour chaque jour la plage horaire de travail et la capacité par créneau.
                                            Si un jour est configuré ici, cette configuration est prioritaire.
                                        </small>
                                        {DAILY_DAYS.map((day) => {
                                            const row = dailyPlanningsByDay[day.dayOfWeek];
                                            return (
                                                <div key={day.dayOfWeek} className="mb-3">
                                                    <div className="mb-1"><b>{day.label}</b></div>
                                                    <div className="d-flex gap-2 mb-1">
                                                        <small className="w-100">Heure de début</small>
                                                        <small className="w-100">Heure de fin</small>
                                                    </div>
                                                    <div className="d-flex gap-2 mb-2">
                                                        <input
                                                            className="form-control"
                                                            type="time"
                                                            value={row?.startTime ? row.startTime.substring(0, 5) : ''}
                                                            onChange={(e) => this.setDailyPlanningValue(day.dayOfWeek, 'startTime', e.target.value || null)}
                                                        />
                                                        <input
                                                            className="form-control"
                                                            type="time"
                                                            value={row?.endTime ? row.endTime.substring(0, 5) : ''}
                                                            onChange={(e) => this.setDailyPlanningValue(day.dayOfWeek, 'endTime', e.target.value || null)}
                                                        />
                                                    </div>
                                                    <div className="d-flex gap-2 mb-1">
                                                        <small className="w-100">{DAILY_SLOT_LABELS.creneau1}</small>
                                                        <small className="w-100">{DAILY_SLOT_LABELS.creneau2}</small>
                                                        <small className="w-100">{DAILY_SLOT_LABELS.creneau3}</small>
                                                        <small className="w-100">{DAILY_SLOT_LABELS.creneau4}</small>
                                                        <small className={`w-100 ${nightSlotEnabled ? '' : 'text-muted'}`}>
                                                            {nightSlotEnabled ? DAILY_SLOT_LABELS.creneau5 : 'C5 (désactivé)'}
                                                        </small>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <SmartInputText value={row?.creneau1} onChange={(value) => this.setDailyPlanningValue(day.dayOfWeek, 'creneau1', value || null)} placeholder={DAILY_SLOT_LABELS.creneau1} />
                                                        <SmartInputText value={row?.creneau2} onChange={(value) => this.setDailyPlanningValue(day.dayOfWeek, 'creneau2', value || null)} placeholder={DAILY_SLOT_LABELS.creneau2} />
                                                        <SmartInputText value={row?.creneau3} onChange={(value) => this.setDailyPlanningValue(day.dayOfWeek, 'creneau3', value || null)} placeholder={DAILY_SLOT_LABELS.creneau3} />
                                                        <SmartInputText value={row?.creneau4} onChange={(value) => this.setDailyPlanningValue(day.dayOfWeek, 'creneau4', value || null)} placeholder={DAILY_SLOT_LABELS.creneau4} />
                                                        {nightSlotEnabled ? (
                                                            <SmartInputText value={row?.creneau5} onChange={(value) => this.setDailyPlanningValue(day.dayOfWeek, 'creneau5', value || null)} placeholder={DAILY_SLOT_LABELS.creneau5} />
                                                        ) : (
                                                            <div className="w-100 d-flex align-items-center justify-content-center border rounded bg-light">
                                                                <small className="text-muted px-1 text-center">Activer le mode nuit</small>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>}
                                    {selectedSector == null && (
                                        <div className="mt-2">
                                            <small>Sélectionne un secteur pour gérer les affectations multiples.</small>
                                        </div>
                                    )}
                    </>}



            </SidebarLayout>
        );
    }
}

export default SetUserGodinLib;
